import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Input,
  InputGroup,
  Table,
  Button,
  DOMHelper,
  Stack,
  DateRangePicker,
  IconButton,
  Tag,
  Pagination
} from 'rsuite';
import SearchIcon from '@rsuite/icons/Search';
import ExportIcon from '@rsuite/icons/Export';
import { MdFilterList } from 'react-icons/md';
import { Icon } from '@rsuite/icons';
import DrawerView from './DrawerView';
import { useDispatch, useSelector } from 'react-redux';
import { fetchBalanceHistory } from '@/services/wfhBalance.service';
import { getAdminUsers } from '@/services/user.service';
import moment from 'moment';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

const DataTable = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { data, loading, pagination } = useSelector(state => state.wfhBalance);
  const { data: users, loading: usersLoading } = useSelector(state => state.users);
  const [showDrawer, setShowDrawer] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [limit, setLimit] = useState(20);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    userId: '',
    operationOwnerId: '',
    authorizedDays: [],
    transactionType: ''
  });
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    end.setHours(23, 59, 59, 999);
    return [start, end];
  });

  // Filter users to exclude those with forgotten: true
  const filteredUsers = users.filter(user => user.forgotten !== true);

  useEffect(() => {
    dispatch(getAdminUsers());
  }, [dispatch]);

  useEffect(() => {
    if (dateRange && dateRange[0]) {
      const startDate = new Date(dateRange[0]);
      startDate.setHours(0, 0, 0, 0);
      const endDate = dateRange[1] ? new Date(dateRange[1]) : new Date(dateRange[0]);
      endDate.setHours(23, 59, 59, 999);

      dispatch(
        fetchBalanceHistory(
          format(startDate, 'yyyy-MM-dd HH:mm:ss'),
          format(endDate, 'yyyy-MM-dd HH:mm:ss'),
          page,
          limit,
          filters
        )
      );
    }
  }, [dateRange, page, limit, filters, dispatch]);

  const handleChangeLimit = dataKey => {
    setPage(1);
    setLimit(dataKey);
  };

  const handleFiltersChange = newFilters => {
    setFilters(newFilters);
    setPage(1);
  };

  const handleResetFilters = () => {
    setFilters({
      userId: '',
      operationOwnerId: '',
      authorizedDays: [],
      transactionType: ''
    });
    setPage(1);
  };

  const getTransactionColor = transactionType => {
    switch (transactionType) {
      case 'reject':
        return 'red';
      case 'submission':
        return 'green';
      case 'cancellation':
        return 'orange';
      default:
        return 'grey';
    }
  };

  const daysRequested = array => {
    if (!array) return '';
    let ch = '';
    array.forEach(operation => {
      ch +=
        moment(operation?.day, 'YYYY-MM-DD').format('dddd') +
        ' ' +
        operation.day?.substring(0, 10).split('-').reverse().join('-') +
        ' ' +
        operation?.slot +
        ', ';
    });
    return ch.slice(0, -2);
  };

  const authorizedDays = array => {
    if (!array) return '';
    return array.join(', ');
  };

  const downloadExcel = (data, name) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    XLSX.writeFile(workbook, `${name}.xlsx`);
  };

  const handleExport = () => {
    const balanceHistoryToExport = data.map(item => {
      return {
        'Full Name': `${item.user?.firstname || ''} ${item.user?.lastname || ''}`,
        'Registration Number': item.user?.registrationNumber || '',
        'Authorized Days': item.authorizedWfhDays ? item.authorizedWfhDays.join(', ') : '',
        'Days Requested': item.operationDates ? daysRequested(item.operationDates) : '',
        'Transaction Type': item.transactionType || '',
        'Old Balance': item.oldBalance || '',
        'New Balance': item.newBalance || '',
        'Operation Owner': `${item.operationOwner?.firstname || ''} ${
          item.operationOwner?.lastname || ''
        }`,
        'Created At': new Date(item.createdAt).toLocaleString()
      };
    });

    downloadExcel(balanceHistoryToExport, 'home-office-balance-history');
  };

  const handleSearch = value => {
    setSearchKeyword(value);
    setPage(1);
  };

  return (
    <>
      <Stack className="table-toolbar" justifyContent="space-between">
        <DateRangePicker
          appearance="default"
          placeholder={t('Select Date Range')}
          style={{ width: 230 }}
          value={dateRange}
          onChange={setDateRange}
          defaultValue={dateRange}
          editable={false}
          ranges={[
            {
              label: t('Last 30 Days'),
              value: [new Date(new Date().setDate(new Date().getDate() - 30)), new Date()]
            },
            {
              label: t('Last 7 Days'),
              value: [new Date(new Date().setDate(new Date().getDate() - 7)), new Date()]
            },
            {
              label: t('Today'),
              value: [new Date(new Date().setDate(new Date().getDate())), new Date()]
            }
          ]}
        />

        <Stack spacing={6}>
          <InputGroup inside>
            <Input placeholder={t('Search')} value={searchKeyword} onChange={handleSearch} />
            <InputGroup.Addon>
              <SearchIcon />
            </InputGroup.Addon>
          </InputGroup>
          <Button appearance="primary" onClick={() => setShowDrawer(true)}>
            <Icon as={MdFilterList} style={{ marginRight: 8 }} /> {t('Filters')}
          </Button>
          <Button appearance="primary" onClick={handleExport}>
            <ExportIcon style={{ marginRight: 8 }} /> {t('Export')}
          </Button>
        </Stack>
      </Stack>
      <>
        <Table
          height={Math.max(getHeight(window) - 200, 400)}
          data={data}
          loading={loading}
          rowHeight={80}
          wordWrap="break-word"
        >
          <Column flexGrow={4}>
            <HeaderCell>{t('First Name & Last Name')}</HeaderCell>
            <Cell>
              {rowData => `${rowData.user?.firstname || ''} ${rowData.user?.lastname || ''}`}
            </Cell>
          </Column>

          <Column flexGrow={4}>
            <HeaderCell>{t('Registration Number')}</HeaderCell>
            <Cell>{rowData => rowData.user?.registrationNumber}</Cell>
          </Column>

          <Column flexGrow={4}>
            <HeaderCell>{t('Authorized Days')}</HeaderCell>
            <Cell>{rowData => authorizedDays(rowData.authorizedWfhDays)}</Cell>
          </Column>

          <Column flexGrow={4}>
            <HeaderCell>{t('Days Requested')}</HeaderCell>
            <Cell style={{ whiteSpace: 'pre-line' }}>
              {rowData => daysRequested(rowData.operationDates)}
            </Cell>
          </Column>

          <Column flexGrow={3}>
            <HeaderCell>{t('Transaction Type')}</HeaderCell>
            <Cell>
              {rowData => (
                <Tag color={getTransactionColor(rowData?.transactionType)}>
                  {t(rowData?.transactionType || 'unknown')}
                </Tag>
              )}
            </Cell>
          </Column>

          <Column flexGrow={3}>
            <HeaderCell>{t('Old Balance')}</HeaderCell>
            <Cell>{rowData => rowData.oldBalance}</Cell>
          </Column>

          <Column flexGrow={3}>
            <HeaderCell>{t('New Balance')}</HeaderCell>
            <Cell>{rowData => rowData.newBalance}</Cell>
          </Column>

          <Column flexGrow={4}>
            <HeaderCell>{t('Operation Owner')}</HeaderCell>
            <Cell>
              {rowData =>
                `${rowData.operationOwner?.firstname || ''} ${
                  rowData.operationOwner?.lastname || ''
                }`
              }
            </Cell>
          </Column>

          <Column flexGrow={3}>
            <HeaderCell>{t('Created At')}</HeaderCell>
            <Cell>{rowData => new Date(rowData.createdAt).toLocaleString()}</Cell>
          </Column>
        </Table>
        <div style={{ padding: 20 }}>
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="xs"
            layout={['total', '-', 'limit', '|', 'pager', 'skip']}
            total={pagination?.total || data.length}
            limitOptions={[10, 20, 30, 50]}
            limit={limit}
            activePage={page}
            onChangePage={setPage}
            onChangeLimit={handleChangeLimit}
          />
        </div>
      </>

      <DrawerView
        open={showDrawer}
        onClose={() => setShowDrawer(false)}
        onFiltersChange={handleFiltersChange}
        onResetFilters={handleResetFilters}
        currentFilters={filters}
        users={filteredUsers}
        usersLoading={usersLoading}
      />
    </>
  );
};

export default DataTable;
